'use client';

import { useState, useEffect } from 'react';

interface LocationProps {
  pathname: string;
  search: string;
  hash: string;
}

/**
 * Custom hook: useLocation
 *
 * This hook provides the current location of the browser window, including
 * the pathname, search query, and hash fragment. It listens for changes
 * to the browser's history state and hash, updating the location state
 * accordingly.
 *
 * Returns:
 * - An object containing:
 *   - pathname: The path of the current URL (e.g., "/home").
 *   - search: The query string of the current URL (e.g., "?id=123").
 *   - hash: The hash fragment of the current URL (e.g., "#section").
 *
 * Behavior:
 * - On initial render, it sets the location state based on the current
 *   browser window's location.
 * - Listens for "popstate" and "hashchange" events to update the location
 *   state when the user navigates or changes the hash.
 * - Cleans up event listeners when the component using this hook unmounts.
 */
const useLocation = (): LocationProps => {
  const [location, setLocation] = useState<LocationProps>({
    pathname: '',
    search: '',
    hash: '',
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const getLocation = (): LocationProps => ({
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
    });

    setLocation(getLocation());

    const handleLocationChange = () => {
      const newLocation = getLocation();

      setLocation(newLocation);
    };

    window.addEventListener('popstate', handleLocationChange);

    window.addEventListener('hashchange', handleLocationChange);

    return () => {
      window.removeEventListener('popstate', handleLocationChange);

      window.removeEventListener('hashchange', handleLocationChange);
    };
  }, []);

  return location;
};

export { useLocation };
